// src/features/users/components/AddGroupHotelsButton.tsx

import { useState } from 'react';
import { useGetList, useTranslate, useNotify } from 'react-admin';
import { Box, TextField, MenuItem, Button } from '@mui/material';
import { PlaylistAdd as PlaylistAddIcon } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';

/**
 * Inline component that lets admins add all hotels from a group
 * to the assignedHotels field in one click.
 * Works for both create and edit: always merges (no duplicates).
 */
export const AddGroupHotelsButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { getValues, setValue } = useFormContext();
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const { data: groups } = useGetList('groups', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
  });

  const { data: hotels } = useGetList('hotels', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
  });

  const handleAddGroupHotels = () => {
    if (!selectedGroupId) {
      notify(translate('resources.users.validation.select_group_first'), { type: 'warning' });
      return;
    }

    const groupHotelIds = (hotels || [])
      .filter((h: any) => h.groupId === selectedGroupId)
      .map((h: any) => h.id);

    if (groupHotelIds.length === 0) {
      notify(translate('resources.users.validation.group_no_hotels'), { type: 'info' });
      return;
    }

    const currentHotels: string[] = getValues('assignedHotels') || [];
    const merged = [...new Set([...currentHotels, ...groupHotelIds])];
    const added = merged.length - currentHotels.length;

    setValue('assignedHotels', merged, { shouldDirty: true });

    if (added > 0) {
      notify(translate('resources.users.validation.group_hotels_added', { count: added }), { type: 'success' });
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
      <TextField
        select
        size="small"
        label={translate('resources.users.fields.groupId')}
        value={selectedGroupId}
        onChange={(e) => setSelectedGroupId(e.target.value)}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-input': { py: '6px', fontSize: '0.8rem' },
          '& .MuiInputLabel-root': { fontSize: '0.8rem' },
        }}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="" disabled>
          {translate('resources.users.validation.select_group_first')}
        </MenuItem>
        {groups?.map((group: any) => (
          <MenuItem key={group.id} value={group.id}>
            {group.name}
          </MenuItem>
        ))}
      </TextField>

      <Button
        variant="outlined"
        size="small"
        onClick={handleAddGroupHotels}
        startIcon={<PlaylistAddIcon sx={{ fontSize: '16px !important' }} />}
        disabled={!selectedGroupId}
        sx={{
          fontSize: '0.75rem',
          py: 0.5,
          px: 1.5,
          minHeight: '32px',
          textTransform: 'none',
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        {translate('resources.users.actions.add_group_hotels')}
      </Button>
    </Box>
  );
};
